import { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const MAX_RETRIES = 2;
const VERIFY_DELAY = 2000;
const LOAD_DELAY = 500;
const OPEN_DELAY = 400;

const InlineHubSpotChat = () => {
  const [showFallback, setShowFallback] = useState(false);
  const retriesRef = useRef(0);
  const timersRef = useRef<number[]>([]);

  const addTimer = (id: number) => {
    timersRef.current.push(id);
  };

  const clearAllTimers = () => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  };

  const getBriefEmail = (): string | null => {
    try {
      const sessionId = new URLSearchParams(window.location.search).get("session_id");
      if (!sessionId) return null;
      const raw = sessionStorage.getItem(`brief_data_${sessionId}`);
      if (!raw) return null;
      return JSON.parse(raw).email || null;
    } catch {
      return null;
    }
  };

  const getInlineSettings = () => {
    const win = window as any;
    const email = getBriefEmail();
    return {
      ...(win.hsConversationsSettings || {}),
      loadImmediately: true,
      inlineEmbedSelector: "#hubspot-inline-chat",
      ...(email ? { identificationEmail: email } : {}),
    };
  };

  const injectHideStyle = () => {
    if (!document.getElementById("hubspot-inline-override")) {
      const style = document.createElement("style");
      style.id = "hubspot-inline-override";
      style.textContent =
        "#hubspot-messages-iframe-container { display: none !important; }";
      document.head.appendChild(style);
    }
  };

  const removeHideStyle = () => {
    document.getElementById("hubspot-inline-override")?.remove();
  };

  const hasInlineContent = () => {
    const container = document.getElementById("hubspot-inline-chat");
    return Boolean(
      container &&
        (container.childElementCount > 0 ||
          container.querySelector("iframe") ||
          container.querySelector("#hubspot-conversations-inline-parent"))
    );
  };

  const applyInlineLayoutFixes = () => {
    const selectors = [
      "#hubspot-inline-chat > div",
      "#hubspot-conversations-inline-parent",
      "#hubspot-conversations-inline-wrapper",
      "#hubspot-conversations-inline-iframe",
      "#hubspot-inline-chat iframe",
    ];

    selectors.forEach((selector) => {
      document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
        element.style.setProperty("width", "100%", "important");
        element.style.setProperty("min-height", "500px", "important");
        element.style.setProperty("height", "500px", "important");
        element.style.setProperty("max-height", "500px", "important");
        element.style.setProperty("position", "relative", "important");
        element.style.setProperty("top", "0", "important");
        element.style.setProperty("left", "0", "important");
        element.style.setProperty("bottom", "0", "important");
        element.style.setProperty("transform", "none", "important");
        element.style.setProperty("border", "none", "important");
      });
    });
  };

  const openInlineWidget = () => {
    const win = window as any;
    try {
      win.HubSpotConversations?.widget?.open();
    } catch (_) {}
    applyInlineLayoutFixes();
  };

  const restoreFloatingWidget = () => {
    removeHideStyle();
    const win = window as any;
    if (win.hsConversationsSettings) {
      delete win.hsConversationsSettings.inlineEmbedSelector;
      win.hsConversationsSettings.loadImmediately = true;
    }

    try {
      if (!win.HubSpotConversations?.widget) return;
      win.HubSpotConversations.widget.remove();
      addTimer(
        window.setTimeout(() => {
          win.hsConversationsSettings = {
            ...(win.hsConversationsSettings || {}),
            loadImmediately: true,
          };
          win.HubSpotConversations?.widget?.load();
          addTimer(window.setTimeout(openInlineWidget, OPEN_DELAY));
        }, 300)
      );
    } catch (_) {}
  };

  const attemptLoad = () => {
    const win = window as any;
    if (!win.HubSpotConversations?.widget) return;

    win.hsConversationsSettings = getInlineSettings();

    try {
      win.HubSpotConversations.widget.remove();
    } catch (_) {}

    addTimer(
      window.setTimeout(() => {
        win.hsConversationsSettings = getInlineSettings();
        win.HubSpotConversations.widget.load();
        addTimer(window.setTimeout(openInlineWidget, OPEN_DELAY));

        addTimer(
          window.setTimeout(() => {
            applyInlineLayoutFixes();

            if (hasInlineContent()) {
              console.log("[InlineHubSpotChat] Inline chat mounted successfully");
              addTimer(window.setTimeout(applyInlineLayoutFixes, 400));
              addTimer(window.setTimeout(applyInlineLayoutFixes, 1200));
              return;
            }

            retriesRef.current++;
            if (retriesRef.current <= MAX_RETRIES) {
              console.log(
                `[InlineHubSpotChat] Retry ${retriesRef.current}/${MAX_RETRIES}`
              );
              attemptLoad();
            } else {
              console.warn(
                "[InlineHubSpotChat] All retries failed, showing fallback"
              );
              setShowFallback(true);
            }
          }, VERIFY_DELAY)
        );
      }, LOAD_DELAY)
    );
  };

  useEffect(() => {
    const win = window as any;
    retriesRef.current = 0;
    setShowFallback(false);

    const email = getBriefEmail();
    if (email) {
      win._hsq = win._hsq || [];
      win._hsq.push(["identify", { email }]);
      win._hsq.push(["trackPageView"]);
    }

    injectHideStyle();

    if (win.HubSpotConversations?.widget) {
      attemptLoad();
    } else {
      const poll = window.setInterval(() => {
        if (win.HubSpotConversations?.widget) {
          clearInterval(poll);
          attemptLoad();
        }
      }, 500);
      addTimer(poll);
      addTimer(
        window.setTimeout(() => {
          clearInterval(poll);
          if (!win.HubSpotConversations?.widget) {
            setShowFallback(true);
          }
        }, 30000)
      );
    }

    return () => {
      clearAllTimers();
      removeHideStyle();
      if (win.hsConversationsSettings) {
        delete win.hsConversationsSettings.inlineEmbedSelector;
        win.hsConversationsSettings.loadImmediately = true;
      }
      try {
        if (win.HubSpotConversations?.widget) {
          win.HubSpotConversations.widget.remove();
          setTimeout(() => {
            win.hsConversationsSettings = {
              ...(win.hsConversationsSettings || {}),
              loadImmediately: true,
            };
            win.HubSpotConversations?.widget?.load();
          }, 300);
        }
      } catch (_) {}
    };
  }, []);

  return (
    <div className="w-full flex flex-col">
      <div
        id="hubspot-inline-chat"
        className="w-full rounded-2xl overflow-hidden shadow-lg border flex-1"
        style={{
          minHeight: "500px",
          height: "max(500px, calc(100vh - 180px))",
          display: showFallback ? "none" : undefined,
        }}
      />
      {showFallback && (
        <div className="rounded-2xl border bg-card p-6 text-center shadow-lg">
          <p className="text-sm text-muted-foreground mb-4">
            Le chat rencontre un problème d'affichage dans cette fenêtre.
          </p>
          <Button onClick={restoreFloatingWidget} className="rounded-xl">
            <MessageCircle className="h-4 w-4 mr-2" />
            Ouvrir le chat
          </Button>
        </div>
      )}
      <style>{`
        #hubspot-inline-chat > div,
        #hubspot-conversations-inline-wrapper,
        #hubspot-inline-chat iframe {
          width: 100% !important;
          min-height: 500px !important;
          height: 500px !important;
          max-height: 500px !important;
          position: relative !important;
          top: 0 !important;
          left: 0 !important;
          bottom: 0 !important;
          transform: none !important;
          border: none !important;
        }

        #hubspot-conversations-inline-parent,
        #hubspot-conversations-inline-iframe {
          min-height: 500px !important;
          height: 500px !important;
          width: 100% !important;
          position: relative !important;
          top: 0 !important;
          left: 0 !important;
          transform: none !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
};

export default InlineHubSpotChat;
